/**
 * blocks-cart-summary.js
 * Standalone JS for the cart-summary block.
 * Handles: note auto-save, formation auto-append, check-cart verification,
 *          checkout aria-busy, cart:updated listener for totals refresh.
 * No dependency on OLD_theme.js custom elements.
 */
(function () {
  'use strict';

  var CART_UPDATE_URL = '/cart/update.js';

  /* --------------------------------------------------------------------------
     ORDER NOTE AUTO-SAVE
     Replaces the <cart-note> custom element with a simple change listener.
     -------------------------------------------------------------------------- */

  function initNoteAutoSave() {
    var textarea = document.querySelector('[data-cart-note]');
    if (!textarea) return;

    var debounceTimer = null;

    textarea.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        saveNote(textarea.value);
      }, 500);
    });
  }

  function saveNote(note) {
    fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: note }),
    }).catch(function (err) {
      console.error('Note save failed:', err);
    });
  }

  /* --------------------------------------------------------------------------
     FORMATION AUTO-APPEND
     Watches for line-item[data-product-type="Formation"] elements and appends
     formation dates to the order note textarea automatically.
     Migrated from OLD_main-cart.liquid inline script.
     -------------------------------------------------------------------------- */

  function initFormationAutoAppend() {
    var textarea = document.querySelector('[data-cart-note]');
    if (!textarea) return;

    // Initial update with slight delay to let DOM settle
    setTimeout(function () {
      updateFormationNote(textarea);
    }, 800);

    // Observe mutations on the cart items container
    var cartItems = document.querySelector('[data-cart-items-block] tbody');
    if (cartItems) {
      var observer = new MutationObserver(function () {
        updateFormationNote(textarea);
      });
      observer.observe(cartItems, { childList: true, subtree: true });
    }
  }

  function updateFormationNote(textarea) {
    var userNote = extractUserNote(textarea.value);
    var formationInfos = [];
    var formationItems = document.querySelectorAll('line-item[data-product-type="Formation"]');

    formationItems.forEach(function (item) {
      var productTitle = item.getAttribute('data-product-title');
      var dateElements = item.querySelectorAll('li.text-sm.text-subdued');

      dateElements.forEach(function (el) {
        var text = el.textContent.trim();
        if (text.includes('Date:')) {
          var dateText = text.replace('Date:', '').trim();
          formationInfos.push(productTitle + ' - ' + dateText);
        }
      });
    });

    var finalNote = userNote;
    if (formationInfos.length > 0) {
      var separator = userNote ? '\n\n' : '';
      finalNote = userNote + separator + '[Formation]\n' + formationInfos.join('\n');
    }

    if (textarea.value !== finalNote) {
      textarea.value = finalNote;
      textarea.dispatchEvent(new Event('change', { bubbles: true }));
      saveNote(finalNote);
    }
  }

  function extractUserNote(fullNote) {
    if (!fullNote) return '';
    var formationMarker = fullNote.indexOf('[Formation]');
    if (formationMarker !== -1) {
      return fullNote.substring(0, formationMarker).trim();
    }
    return fullNote;
  }

  /* --------------------------------------------------------------------------
     CHECK CART (VERIFICATION)
     Allows the customer to send their cart for verification via contact form.
     -------------------------------------------------------------------------- */

  function initCheckCart() {
    var container = document.querySelector('[data-check-cart]');
    if (!container) return;

    var showBtn = container.querySelector('[data-check-cart-show]');
    var confirmation = container.querySelector('[data-check-cart-confirmation]');
    var yesBtn = container.querySelector('[data-check-cart-yes]');
    var noBtn = container.querySelector('[data-check-cart-no]');
    var form = container.querySelector('[data-check-cart-form]');
    var noteInput = container.querySelector('[data-check-cart-note]');
    var messageInput = form ? form.querySelector('[name="contact[message]"]') : null;
    var cartIdInput = form ? form.querySelector('[name="contact[cart_id]"]') : null;

    if (showBtn && confirmation) {
      showBtn.addEventListener('click', function () {
        confirmation.classList.add('is-visible');
        showBtn.style.display = 'none';
      });
    }

    if (noBtn && confirmation && showBtn) {
      noBtn.addEventListener('click', function () {
        confirmation.classList.remove('is-visible');
        showBtn.style.display = '';
      });
    }

    if (yesBtn && form) {
      yesBtn.addEventListener('click', function () {
        // Build cart description for the message
        var rows = document.querySelectorAll('[data-csv-item]');
        var cartDescription = '';
        rows.forEach(function (row) {
          var name = row.getAttribute('data-csv-name') || '';
          var qty = row.getAttribute('data-csv-qty') || '0';
          var sku = row.getAttribute('data-csv-sku') || '';
          cartDescription += name + ' (SKU: ' + sku + ') x' + qty + '\n';
        });

        var userMessage = noteInput ? noteInput.value : '';
        var fullMessage = cartDescription + '\n---\nMessage client: ' + userMessage;

        if (messageInput) messageInput.value = fullMessage;

        // Generate a simple cart ID
        if (cartIdInput) {
          cartIdInput.value = 'CART-' + Date.now();
        }

        form.submit();
      });
    }
  }

  /* --------------------------------------------------------------------------
     CHECKOUT BUTTON LOADER
     Affiche un spinner à la place du texte "Commander" pendant la redirection
     vers le checkout (délai POST /cart -> page de paiement).
     On écoute le `submit` du formulaire (et non le clic) : ainsi le loader ne
     s'affiche que lorsque le checkout part réellement. Quand l'overlay tombola
     intercepte le clic (preventDefault), aucun submit n'a lieu -> pas de loader.
     Fonctionne indépendamment de la tombola (activée ou non).
     -------------------------------------------------------------------------- */

  var CHECKOUT_SPINNER_SVG =
    '<svg class="cart-summary__checkout-spinner" width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="31.4 31.4" />' +
    '</svg>';

  function setCheckoutLoading(btn) {
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('aria-label', 'Redirection vers le paiement…');
    btn.innerHTML = CHECKOUT_SPINNER_SVG;
  }

  function initCheckoutLoader() {
    var form = document.querySelector('.cart-summary__form');
    if (!form) return;

    var checkoutBtn = form.querySelector('button[name="checkout"]');
    if (!checkoutBtn) return;

    form.addEventListener('submit', function (e) {
      // Ignorer les soumissions déclenchées par un autre bouton du form
      // (ex. bouton B2B #wcp-30-open). event.submitter peut être null sur
      // une soumission programmatique sans bouton -> on affiche alors le loader.
      if (e.submitter && e.submitter.name !== 'checkout') return;
      setCheckoutLoading(checkoutBtn);
    });
  }

  /* --------------------------------------------------------------------------
     CART:UPDATED LISTENER
     When items are modified in the main-cart block, refresh totals.
     -------------------------------------------------------------------------- */

  function initCartUpdateListener() {
    document.addEventListener('cart:updated', function () {
      // Page reload handles fresh Liquid data (simplest approach with WCP)
      // If we want partial refresh, we'd fetch /cart.js and update DOM
      // For now, main-cart.js already triggers page reload
    });
  }

  /* --------------------------------------------------------------------------
     INIT
     -------------------------------------------------------------------------- */

  function init() {
    initNoteAutoSave();
    initFormationAutoAppend();
    initCheckCart();
    initCheckoutLoader();
    initCartUpdateListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
